import React, { useCallback, useMemo } from 'react';
import { EuiFlexGroup, EuiFlexItem, EuiFormRow, EuiFieldNumber, EuiButton } from '@elastic/eui';
import { Controller } from 'react-hook-form';
import ComboBoxField from '../../../../components/Form/ComboBoxField';
import PropTypes from 'prop-types';

const OptimizedVariantSelect = ({
    item,
    index,
    itemVariantsMap,
    errors,
    control,
    isLoadingItems,
    getSelectedVariantOptions,
    handleVariantChange,
    deleteSpecificRequiredItems,
}) => {
    const variantOptions = useMemo(() => {
        return itemVariantsMap[item.item_id] || [];
    }, [itemVariantsMap, item.item_id]);

    const selectedVariants = useMemo(() => {
        return getSelectedVariantOptions(item, index);
    }, [getSelectedVariantOptions, item, index]);

    const hasVariants = variantOptions.length > 0;

    const handleVariantSelection = useCallback(
        (selectedOptions) => {
            handleVariantChange(selectedOptions, index);
        },
        [handleVariantChange, index]
    );

    return (
        <EuiFlexGroup alignItems='center' gutter='2xl' style={{ height: '100%' }}>
            <EuiFlexItem grow={false} style={{ width: '230px', marginLeft: '100px' }}>
                <ComboBoxField
                    key={`variant-select-${item.item_id}-${index}`}
                    options={variantOptions}
                    selectedOptions={selectedVariants}
                    onChange={handleVariantSelection}
                    errors={errors}
                    label='Select variants'
                    name={`required_items[${index}].variant_option_id`}
                    placeholder={hasVariants ? 'Select variants' : 'No variants'}
                    rules={{
                        validate: () =>
                            hasVariants ? selectedVariants?.length > 0 || 'Please select at least one variant' : true,
                    }}
                    control={control}
                    isDisabled={!hasVariants}
                    isLoading={isLoadingItems}
                />
            </EuiFlexItem>

            <EuiFlexItem grow={false} style={{ width: '420px' }}>
                <EuiFormRow
                    label={item.label}
                    isInvalid={errors?.required_items?.[index]?.quantity}
                    error={
                        errors?.required_items?.[index]?.quantity ? errors.required_items[index].quantity.message : ''
                    }
                >
                    <Controller
                        render={({ field }) => (
                            <EuiFieldNumber
                                placeholder='Please enter the minimum quantity required'
                                value={field.value}
                                onChange={field.onChange}
                            />
                        )}
                        name={`required_items[${index}].quantity`}
                        control={control}
                        rules={{
                            required: 'Please enter the minimum quantity required',
                            validate: (value) => value >= 0 || 'Please enter value greater than 0',
                        }}
                        fullWidth={true}
                        min={1}
                        required={errors?.required_items?.quantity?.hasOwnProperty(index)}
                    />
                </EuiFormRow>
            </EuiFlexItem>

            <EuiFlexItem grow={false} style={{ marginBottom: '0px' }}>
                <EuiButton
                    iconType='minusInCircle'
                    style={{ textAlign: 'left' }}
                    onClick={() => deleteSpecificRequiredItems(item.item_id)}
                >
                    Remove
                </EuiButton>
            </EuiFlexItem>
        </EuiFlexGroup>
    );
};

OptimizedVariantSelect.propTypes = {
    item: PropTypes.object.isRequired,
    index: PropTypes.number.isRequired,
    itemVariantsMap: PropTypes.object.isRequired,
    errors: PropTypes.object,
    control: PropTypes.object.isRequired,
    isLoadingItems: PropTypes.bool,
    getSelectedVariantOptions: PropTypes.func.isRequired,
    handleVariantChange: PropTypes.func.isRequired,
    deleteSpecificRequiredItems: PropTypes.func.isRequired,
};

export default React.memo(OptimizedVariantSelect);
